import { useState } from 'react'
import type { PartialRange } from '../types'

export interface PartialRangeEditor {
  expanded: boolean
  toggle: () => void
  hasRange: boolean
  localRange: [number, number]
  setLocalRange: (v: [number, number]) => void
  commit: (v: [number, number]) => void
  clear: () => void
}

/**
 * Owns the editor's transient state so a totem row and a member row can share
 * one control without duplicating the expand/commit/clear dance. Dragging the
 * slider back to the full set is the same gesture as clearing — a range that
 * covers everything is not a range.
 */
export function usePartialRangeEditor({
  range,
  duration,
  onChange,
}: {
  range: PartialRange | null | undefined
  duration: number
  onChange: (range: PartialRange | null) => void
}): PartialRangeEditor {
  const [expanded, setExpanded] = useState(false)
  const [localRange, setLocalRange] = useState<[number, number]>([
    range?.startMin ?? 0,
    range?.endMin ?? duration,
  ])
  const hasRange = !!range

  const clear = () => {
    onChange(null)
    setLocalRange([0, duration])
    setExpanded(false)
  }

  return {
    expanded,
    hasRange,
    localRange,
    setLocalRange,
    clear,
    toggle: () => {
      if (expanded) {
        setExpanded(false)
        if (!hasRange) setLocalRange([0, duration])
      } else {
        setExpanded(true)
      }
    },
    commit: ([startMin, endMin]) => {
      if (startMin === 0 && endMin === duration) clear()
      else onChange({ startMin, endMin })
    },
  }
}
